/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useDispatch } from 'react-redux'
import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom'
import { useAppSelector } from 'store/hooks'

import {
    ChartSquareBarIcon,
    /* ChatAlt2Icon, */
    DocumentTextIcon/* ,
    GlobeAltIcon,
    ServerIcon,
    ShoppingBagIcon */,
    UserIcon
} from '@heroicons/react/solid'

import { Paths } from 'models/enums'

/**********************************************************************************************************
 *   COMPONENT IMPORTS
 **********************************************************************************************************/
import { Frame, Status } from '@nexigendigital/nxui'
import { setCartOpen } from 'store/slices/shopSlice'
import { setSupportOpen } from 'store/slices/supportSlice'

/**********************************************************************************************************
 *   CONTAINERS
 **********************************************************************************************************/
import Dashboard from 'containers/dashboard/dashboard'
import Domains from 'containers/domains/domains'
import Services from 'containers/services/services'
import Support from 'containers/support/support'
import Billing from 'containers/billing/billing'
import Account from 'containers/account/account'
import Shop from 'containers/shop/shop'
import Checkout from 'containers/shop/checkout/checkout'
import OrderComplete from 'containers/shop/checkout/orderComplete'
import Cart from 'containers/shop/cart/lightbox/cart'
import { Invoice } from 'components/invoice'

/**********************************************************************************************************
 *   API
 **********************************************************************************************************/
import { useLogoutMutation } from 'api/app'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function UserRouter() {
    const dispatch = useDispatch()
    const location = useLocation()
    const navigate = useNavigate()
    const {
        appSession: { user, account },
        appSettings: { containers, provider: { provider_name, application_name, logo_url } }
    } = useAppSelector((state) => state.app)
    const [logout] = useLogoutMutation()

    function checkLocation(link) {
        return location.pathname === link
    }

    /* function fetchIcon() {
        switch (true) {
            case checkLocation('/dashboard'):
                return <ChartSquareBarIcon width={'20'} />
            case checkLocation('/domains'):
                return <GlobeAltIcon width={'20'} />
            case checkLocation('/services'):
                return <ServerIcon width={'20'} />
            case checkLocation('/support'):
                return <ChatAlt2Icon width={'20'} />
            case checkLocation('/billing'):
                return <DocumentTextIcon width={'20'} />
            case checkLocation('/account'):
                return <UserIcon width={'20'} />
            case checkLocation('/shop'):
                return <ShoppingBagIcon width={'20'} />
            default:
                return null
        }
    } */

    function buildNavigation() {
        return [
            [
                { icon: <ChartSquareBarIcon width={'20px'} />, label: 'Dashboard', onClick: () => navigate('/dashboard'), active: checkLocation('/dashboard') }
            ], /* [
                { icon: <GlobeAltIcon width={'20px'} />, label: 'Domains', onClick: () => navigate('/domains'), active: checkLocation('/domains') },
                { icon: <ServerIcon width={'20px'} />, label: 'Services', onClick: () => navigate('/services'), active: checkLocation('/services') }
            ], */ [
                { icon: <DocumentTextIcon width={'20px'} />, label: 'Billing', onClick: () => navigate('/billing'), active: checkLocation('/billing') },
                { icon: <UserIcon width={'20px'} />, label: 'Account', onClick: () => navigate('/account'), active: checkLocation('/account') }
            ]/* , [
                { icon: <ShoppingBagIcon width={'20px'} />, label: 'Shop', onClick: () => navigate('/shop'), active: checkLocation('/shop') },
                { icon: <ChatAlt2Icon width={'20px'} />, label: 'Support', onClick: () => navigate('/support'), active: checkLocation('/support') }
            ] */
        ]
    }

    function buildRoutes() {
        if (containers && typeof containers === 'object') {
            const routes = [
                { path: 'login/*', element: <Navigate to='/dashboard' replace /> },
                { path: '/', element: <Navigate to='/dashboard' replace /> },
                { path: '*', element: <Status status={404} back={() => navigate(-1)} /> },
                { path: 'checkout/*', element: <Checkout /> },
                { path: 'order/*', element: <OrderComplete /> }
            ]

            function fetchElement(key) {
                switch (key) {
                    case 'dashboard': return <Dashboard />
                    case 'domains': return <Domains />
                    case 'services': return <Services />
                    case 'billing': return <Billing />
                    case 'account': return <Account />
                    case 'shop': return <Shop />
                    default: return null
                }
            }

            Object.keys(containers).forEach((key) => {
                if (containers[key] && Paths[key] && Paths[key].route) {
                    routes.push({
                        ...Paths[key].route,
                        element: fetchElement(key)
                    })
                }
            })
            return routes
        }

        return [
            { path: 'login/*', element: <Navigate to='/dashboard' replace /> },
            { path: 'dashboard', element: <Dashboard /> },
            { path: 'account', element: <Navigate to='/account/twofactor' replace /> },
            { path: 'account/*', element: <Account /> },
            // { path: 'domains/*', element: <Domains /> },
            // { path: 'services/*', element: <Services /> },
            // { path: 'support', element: <Support /> },
            { path: 'billing', element: <Navigate to='/billing/invoices' replace /> },
            { path: 'billing/*', element: <Billing /> },
            // { path: 'shop/*', element: <Shop /> },
            // { path: 'checkout/*', element: <Checkout /> },
            // { path: 'order/*', element: <OrderComplete /> },
            { path: '/', element: <Navigate to='/dashboard' replace /> },
            { path: '*', element: <Status status={404} back={() => navigate(-1)} /> }
        ]
    }

    return (
        <>
            <Frame
                company={{
                    src: logo_url,
                    alt: provider_name,
                    application: application_name
                }}
                profile={{
                    user,
                    account: {
                        company: account.company_name,
                        account_number: account.account_number,
                        role: {
                            name: account.role.name
                        }
                    },
                    redirect: () => navigate('/account')
                }}
                support={() => dispatch(setSupportOpen(true))}
                shop={() => dispatch(setCartOpen(true))}
                nav={buildNavigation()}
                content={{
                    render: useRoutes(buildRoutes())
                }}
                logout={() => logout()}
            />

            <Invoice />
            <Cart />
            <Support />
        </>
    )
}
